import { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { X, Trash2, Loader2, Minus, Plus, Tag, Eye, EyeOff, ChevronDown, ChevronUp, Package } from "lucide-react";
import LessonArtwork from "@/components/LessonArtwork";
import { useCart } from "@/contexts/CartContext";
import ParcelLockerSelector from "@/components/ParcelLockerSelector";
import type { ParcelLocation } from "@/hooks/useParcelLocations";
import { getCourierLabel } from "@/hooks/useParcelLocations";
import type { TerminalData } from "@/hooks/useBookCheckout";
import { useAuth } from "@/contexts/AuthContext";
import { useBookCheckout, ShippingData } from "@/hooks/useBookCheckout";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import visaLogo from "@/assets/payment/visa.svg";
import mastercardLogo from "@/assets/payment/mastercard.png";
import googlePayLogo from "@/assets/payment/googlepay.png";
import applePayLogo from "@/assets/payment/applepay.png";
import payseraLogo from "@/assets/payment/paysera.png";
import { trackViewCart, trackBeginCheckout, trackAddPaymentInfo, cartItemToAnalytics } from "@/lib/analytics";
import UpsellRecommendations from "@/components/UpsellRecommendations";
import { useCurrency } from "@/contexts/CurrencyContext";
import { cartItemPrice } from "@/lib/pricing";

interface CartDrawerProps {
  open: boolean;
  onClose: () => void;
}

interface ShippingZone {
  name: string;
  countries: string[];
  price: number;
}

const COUNTRY_OPTIONS = [
  { code: "LT", label: "Lithuania" },
  { code: "LV", label: "Latvia" },
  { code: "EE", label: "Estonia" },
  { code: "PL", label: "Poland" },
  { code: "DE", label: "Germany" },
  { code: "FR", label: "France" },
  { code: "ES", label: "Spain" },
  { code: "IT", label: "Italy" },
  { code: "NL", label: "Netherlands" },
  { code: "BE", label: "Belgium" },
  { code: "AT", label: "Austria" },
  { code: "FI", label: "Finland" },
  { code: "SE", label: "Sweden" },
  { code: "DK", label: "Denmark" },
  { code: "IE", label: "Ireland" },
  { code: "PT", label: "Portugal" },
  { code: "CZ", label: "Czech Republic" },
  { code: "SK", label: "Slovakia" },
  { code: "HU", label: "Hungary" },
  { code: "RO", label: "Romania" },
  { code: "BG", label: "Bulgaria" },
  { code: "HR", label: "Croatia" },
  { code: "SI", label: "Slovenia" },
  { code: "GR", label: "Greece" },
  { code: "GB", label: "United Kingdom" },
  { code: "NO", label: "Norway" },
  { code: "CH", label: "Switzerland" },
  { code: "US", label: "United States" },
];

const COURIER_OPTIONS = [
  { code: "lp_express", label: "LP Express" },
];

const CartDrawer = ({ open, onClose }: CartDrawerProps) => {
  const {
    items, removeItem, updateQuantity, total, discountedTotal, count,
    discount, discountLoading, discountError, applyDiscount, removeDiscount,
  } = useCart();
  const { user } = useAuth();
  const { currency, format: fmt } = useCurrency();
  const { initiateCheckout, isLoading } = useBookCheckout();
  const isMobile = useIsMobile();

  // Form state
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [country, setCountry] = useState("LT");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState<"courier" | "address">("courier");
  const [selectedCourier, setSelectedCourier] = useState("lp_express");
  const [selectedTerminal, setSelectedTerminal] = useState<ParcelLocation | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<"stripe" | "paysera" | "googlepay" | "applepay">("stripe");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [createAccount, setCreateAccount] = useState(false);
  const [accountPassword, setAccountPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Shipping zones
  const [shippingZones, setShippingZones] = useState<ShippingZone[]>([]);

  useEffect(() => {
    if (!open) return;
    const fetchZones = async () => {
      const { data } = await supabase
        .from("store_settings")
        .select("value")
        .eq("key", "shipping")
        .maybeSingle();
      if (data?.value) {
        const val = data.value as any;
        setShippingZones(val.zones || []);
      }
    };
    fetchZones();
  }, [open]);

  // Lock body scroll when checkout is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Track view_cart when drawer opens with items
    if (items.length > 0) {
      const analyticsItems = items.map(i => cartItemToAnalytics(i.product, i.quantity));
      const cartTotal = items.reduce((s, i) => s + i.product.price * i.quantity, 0);
      trackViewCart(analyticsItems, cartTotal);
    }
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Pre-fill email for logged-in users
  useEffect(() => {
    if (user?.email && !email) setEmail(user.email);
  }, [user]);

  const bookItems = items.filter((i) => i.product.type === "book");
  const lessonItems = items.filter((i) => i.product.type === "lesson");
  const zemelapisItems = items.filter((i) => i.product.type === "zemelapis");
  const hasPhysicalBooks = bookItems.length > 0;
  const hasLessons = lessonItems.length > 0;
  const hasZemelapis = zemelapisItems.length > 0;
  const isLoggedIn = !!user;

  // Force account creation for lessons or zemelapis (need user_id)
  useEffect(() => {
    if ((hasLessons || hasZemelapis) && !isLoggedIn) setCreateAccount(true);
  }, [hasLessons, hasZemelapis, isLoggedIn]);

  // Parcel-locker delivery is LT-only — outside Lithuania force address delivery
  useEffect(() => {
    if (country !== "LT" && deliveryMethod !== "address") {
      setDeliveryMethod("address");
      setSelectedTerminal(null);
    }
  }, [country, deliveryMethod]);




  // Shipping cost calculation — zones store English country names, map code→English name
  const CODE_TO_EN: Record<string, string> = {
    LT: "Lithuania", LV: "Latvia", EE: "Estonia", PL: "Poland", DE: "Germany",
    FR: "France", ES: "Spain", IT: "Italy", NL: "Netherlands", BE: "Belgium",
    AT: "Austria", FI: "Finland", SE: "Sweden", DK: "Denmark", IE: "Ireland",
    PT: "Portugal", CZ: "Czech Republic", SK: "Slovakia", HU: "Hungary", RO: "Romania",
    BG: "Bulgaria", HR: "Croatia", SI: "Slovenia", GR: "Greece", GB: "United Kingdom",
    NO: "Norway", CH: "Switzerland", US: "United States",
  };
  const shippingCost = useMemo(() => {
    if (!hasPhysicalBooks) return 0;
    const enName = CODE_TO_EN[country] || country;
    const zone = shippingZones.find(z => z.countries.includes(enName) || z.countries.includes(country));
    return zone?.price ?? 0;
  }, [country, shippingZones, hasPhysicalBooks]);

  // Currency-aware display amounts (catalogue prices per currency)
  const currencyTotal = useMemo(
    () => items.reduce((sum, i) => sum + cartItemPrice(i.product, currency) * i.quantity, 0),
    [items, currency]
  );
  const discountRatio = total > 0 ? discountedTotal / total : 1;
  const currencyDiscountedTotal = currencyTotal * discountRatio;

  const finalTotal = currencyDiscountedTotal + shippingCost;

  if (!open) return null;

  // Validation
  const getValidationErrors = (): string[] => {
    const errors: string[] = [];
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) errors.push("Enter a valid email");
    if (!agreeTerms) errors.push("You must agree to the terms");
    if (hasPhysicalBooks) {
      if (!name.trim()) errors.push("Enter your full name");
      if (!phone.trim()) errors.push("Enter a phone number");
      if (country === "LT" && deliveryMethod === "courier" && !selectedTerminal) errors.push("Choose a parcel locker");
      if (country !== "LT" || deliveryMethod === "address") {
        if (!address.trim()) errors.push("Enter an address");
        if (!city.trim()) errors.push("Enter a city");
        if (!postalCode.trim()) errors.push("Enter a postal code");
      }
    }
    if (createAccount && !isLoggedIn && accountPassword.length < 8) errors.push("Password too short (min. 8 characters)");
    return errors;
  };

  const isFormValid = () => getValidationErrors().length === 0;

  const handleSubmit = async () => {
    const errors = getValidationErrors();
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors([]);

    const books = bookItems.flatMap(item =>
      Array.from({ length: item.quantity }, () => ({
        bookId: item.product.id,
        bookTitle: item.product.title,
        price: item.product.price,
      }))
    );
    const lessons = lessonItems.map(item => ({
      lessonId: item.product.id,
      lessonTitle: item.product.title,
      price: item.product.price,
    }));
    const zemelapis = zemelapisItems.flatMap(item =>
      Array.from({ length: item.quantity }, () => ({
        tier: (item.product.zemelapisTier || "single") as "single" | "gift_pair" | "gift_single",
        senderName: item.product.giftSenderName,
        recipientName: item.product.giftRecipientName,
        recipientEmail: item.product.giftRecipientEmail,
        personalMessage: item.product.giftMessage,
      }))
    );

    const method = paymentMethod === "googlepay" || paymentMethod === "applepay" ? "stripe" : paymentMethod;

    const terminalData: TerminalData | undefined =
      deliveryMethod === "courier" && selectedTerminal
        ? {
            id: selectedTerminal.id,
            identifier: selectedTerminal.identifier,
            name: selectedTerminal.name,
            courier_code: selectedTerminal.courier_code,
            address: selectedTerminal.address,
            city: selectedTerminal.city,
            postal_code: selectedTerminal.postal_code,
          }
        : undefined;

    const shippingData: ShippingData | undefined =
      hasPhysicalBooks
        ? {
            name,
            address: deliveryMethod === "address" ? address : (selectedTerminal?.address || ""),
            city: deliveryMethod === "address" ? city : (selectedTerminal?.city || ""),
            postalCode: deliveryMethod === "address" ? postalCode : (selectedTerminal?.postal_code || ""),
            countryCode: country,
            phone,
            email,
          }
        : undefined;

    const needsAccountCreation = !isLoggedIn && createAccount;

    const analyticsItems = items.map(i => cartItemToAnalytics(i.product, i.quantity));
    trackBeginCheckout(analyticsItems, finalTotal, discount?.code);
    trackAddPaymentInfo(method, finalTotal);

    await initiateCheckout({
      books,
      lessons,
      zemelapis,
      method,
      discountCode: discount?.code,
      currency,
      shipping: shippingData,
      phone: hasPhysicalBooks ? phone : undefined,
      terminal: terminalData,
      shippingCost: hasPhysicalBooks ? shippingCost : undefined,
      createAccount: needsAccountCreation || undefined,
      accountEmail: needsAccountCreation ? email : undefined,
      accountPassword: needsAccountCreation ? accountPassword : undefined,
    });
  };


  // --- ORDER SUMMARY (right column / mobile collapsible) ---
  const orderSummary = (
    <div className="space-y-4">
      {/* Items */}
      <div className="space-y-3">
        {items.map((item) => (
          <div key={item.product.id} className="flex gap-3">
            <div className="w-14 h-[72px] flex-shrink-0 rounded-lg overflow-hidden bg-muted/30 border border-border">
              {item.product.type === "lesson" ? (
                <LessonArtwork title={item.product.title} seed={item.product.id} className="w-full h-full" />
              ) : item.product.cover_url ? (
                <img src={item.product.cover_url} alt={item.product.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                  📖
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground leading-tight line-clamp-2">{item.product.title}</p>
              {item.product.author && <p className="text-xs text-muted-foreground mt-0.5">{item.product.author}</p>}
              {item.product.type === "book" && (
                <div className="flex items-center gap-2 mt-1.5">
                  <button
                    onClick={() => item.quantity <= 1 ? removeItem(item.product.id) : updateQuantity(item.product.id, item.quantity - 1)}
                    className="w-6 h-6 rounded flex items-center justify-center text-muted-foreground hover:text-foreground border border-border hover:bg-muted/50 transition-colors"
                  >
                    <Minus className="w-3 h-3" />
                  </button>
                  <span className="text-xs font-medium text-foreground w-5 text-center">{item.quantity}</span>
                  <button
                    onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                    className="w-6 h-6 rounded flex items-center justify-center text-muted-foreground hover:text-foreground border border-border hover:bg-muted/50 transition-colors"
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              <span className="text-sm font-semibold text-foreground">{fmt(cartItemPrice(item.product, currency) * item.quantity)}</span>
              <button onClick={() => removeItem(item.product.id)} className="text-muted-foreground hover:text-destructive transition-colors">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Discount */}
      <div className="border-t border-border pt-3 space-y-2">
        {discount ? (
          <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-primary/10 border border-primary/20">
            <span className="flex items-center gap-1.5 text-xs text-primary font-semibold">
              <Tag className="w-3 h-3" />
              {discount.code} ({discount.type === "percentage" ? `${discount.value}%` : `€${discount.value}`})
            </span>
            <button onClick={removeDiscount} className="text-primary/60 hover:text-primary text-xs">✕</button>
          </div>
        ) : (
          <div className="flex gap-2">
            <input
              type="text"
              value={discountCode}
              onChange={(e) => setDiscountCode(e.target.value)}
              placeholder="Discount code"
              className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
            />
            <button
              onClick={() => applyDiscount(discountCode)}
              disabled={discountLoading || !discountCode.trim()}
              className="px-3 py-2 rounded-lg bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/20 transition-colors disabled:opacity-40"
            >
              {discountLoading ? "..." : "Apply"}
            </button>
          </div>
        )}
        {discountError && <p className="text-xs text-destructive">{discountError}</p>}
      </div>

      {/* Totals */}
      <div className="border-t border-border pt-3 space-y-1.5">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Subtotal</span>
          <span className="text-foreground">
            {discount && <span className="line-through text-muted-foreground mr-1.5">{fmt(currencyTotal)}</span>}
            {fmt(currencyDiscountedTotal)}
          </span>
        </div>
        {hasPhysicalBooks && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Shipping</span>
            <span className="text-foreground">{shippingCost > 0 ? fmt(shippingCost) : "—"}</span>
          </div>
        )}
        <div className="flex justify-between text-base font-bold pt-1.5 border-t border-border">
          <span className="text-foreground">Total</span>
          <span className="text-foreground">{fmt(finalTotal)}</span>
        </div>
      </div>
    </div>
  );

  // --- LEFT COLUMN ---
  const leftColumn = (
    <div className="space-y-6">
      {/* Contact */}
      <section className="space-y-3">
        <h2 className="text-base font-semibold text-foreground">Contact</h2>
        <div>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email *"
            className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
          />
        </div>
      </section>

      {/* Shipping (physical books only) */}
      {hasPhysicalBooks && (
        <section className="space-y-3">
          <h2 className="text-base font-semibold text-foreground">Shipping</h2>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name *"
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
              />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
              >
                {COUNTRY_OPTIONS.map(c => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <input
                type="text"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                placeholder="Postal code *"
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
              />
            </div>
            <div className="col-span-2">
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone * (e.g. +370 600 00000)"
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
              />
            </div>
          </div>
        </section>
      )}

      {/* Delivery method */}
      {hasPhysicalBooks && (
        <section className="space-y-3">
          <h2 className="text-base font-semibold text-foreground">Delivery method</h2>
          <div className="space-y-2">
            {/* Courier options (only for LT) */}
            {country === "LT" && COURIER_OPTIONS.map(c => (
              <label
                key={c.code}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  deliveryMethod === "courier" && selectedCourier === c.code
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/30"
                }`}
                onClick={() => {
                  setDeliveryMethod("courier");
                  setSelectedCourier(c.code);
                  setSelectedTerminal(null);
                }}
              >
                <input
                  type="radio"
                  name="delivery"
                  checked={deliveryMethod === "courier" && selectedCourier === c.code}
                  onChange={() => {}}
                  className="accent-primary w-4 h-4"
                />
                <Package className="w-4 h-4 text-muted-foreground" />
                <span className="flex-1 text-sm text-foreground">{c.label}</span>
                <span className="text-sm font-medium text-foreground">{fmt(shippingCost)}</span>
              </label>
            ))}

            {/* Show parcel selector when courier selected */}
            {country === "LT" && deliveryMethod === "courier" && (
              <div className="pl-7">
                <ParcelLockerSelector
                  selected={selectedTerminal}
                  onSelect={setSelectedTerminal}
                  courierFilter={selectedCourier}
                />
              </div>
            )}

            {/* Address delivery */}
            <label
              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                deliveryMethod === "address" ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"
              }`}
              onClick={() => { setDeliveryMethod("address"); setSelectedTerminal(null); }}
            >
              <input
                type="radio"
                name="delivery"
                checked={deliveryMethod === "address"}
                onChange={() => {}}
                className="accent-primary w-4 h-4"
              />
              <span className="flex-1 text-sm text-foreground">Delivery to address</span>
              <span className="text-sm font-medium text-foreground">{fmt(shippingCost)}</span>
            </label>

            {deliveryMethod === "address" && (
              <div className="pl-7 space-y-2">
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Address *"
                  className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
                />
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="City *"
                  className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
                />
              </div>
            )}
          </div>
        </section>
      )}

      {/* Payment */}
      <section className="space-y-3">
        <h2 className="text-base font-semibold text-foreground">Payment</h2>
        <div className="space-y-2">
          <label
            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
              paymentMethod === "stripe" ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"
            }`}
            onClick={() => setPaymentMethod("stripe")}
          >
            <input type="radio" name="payment" checked={paymentMethod === "stripe"} onChange={() => {}} className="accent-primary w-4 h-4" />
            <span className="flex-1 text-sm text-foreground">Payment card</span>
            <div className="flex items-center gap-1.5">
              <img src={visaLogo} alt="Visa" className="h-5 w-auto opacity-70" />
              <img src={mastercardLogo} alt="Mastercard" className="h-5 w-auto opacity-70" />
            </div>
          </label>

          {currency === "EUR" && (
          <label
            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
              paymentMethod === "paysera" ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"
            }`}
            onClick={() => setPaymentMethod("paysera")}
          >
            <input type="radio" name="payment" checked={paymentMethod === "paysera"} onChange={() => {}} className="accent-primary w-4 h-4" />
            <span className="flex-1 text-sm text-foreground">Paysera (banks)</span>
            <img src={payseraLogo} alt="Paysera" className="h-5 w-auto opacity-70" />
          </label>
          )}

          <label
            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
              paymentMethod === "googlepay" ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"
            }`}
            onClick={() => setPaymentMethod("googlepay")}
          >
            <input type="radio" name="payment" checked={paymentMethod === "googlepay"} onChange={() => {}} className="accent-primary w-4 h-4" />
            <span className="flex-1 text-sm text-foreground">Google Pay</span>
            <img src={googlePayLogo} alt="Google Pay" className="h-5 w-auto opacity-70" />
          </label>

          <label
            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
              paymentMethod === "applepay" ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"
            }`}
            onClick={() => setPaymentMethod("applepay")}
          >
            <input type="radio" name="payment" checked={paymentMethod === "applepay"} onChange={() => {}} className="accent-primary w-4 h-4" />
            <span className="flex-1 text-sm text-foreground">Apple Pay</span>
            <img src={applePayLogo} alt="Apple Pay" className="h-5 w-auto opacity-70" />
          </label>
        </div>
      </section>

      {/* Terms */}
      <div className="space-y-3">
        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={agreeTerms}
            onChange={(e) => setAgreeTerms(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-primary rounded"
          />
          <span className="text-xs text-muted-foreground">
            I agree to the{" "}
            <a href="/terms" target="_blank" className="text-primary underline">terms of use</a>
            {" "}ir{" "}
            <a href="/privacy" target="_blank" className="text-primary underline">privacy policy</a>
          </span>
        </label>

        {/* Create account */}
        {!isLoggedIn && (
          <div className="space-y-2">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={createAccount}
                onChange={(e) => !(hasLessons || hasZemelapis) && setCreateAccount(e.target.checked)}
                disabled={hasLessons || hasZemelapis}
                className="mt-0.5 h-4 w-4 accent-primary rounded"
              />
              <div>
                <span className="text-sm text-foreground font-medium">Create an account</span>
                {(hasLessons || hasZemelapis) ? (
                  <p className="text-xs text-primary/80 mt-0.5">Required for {hasLessons ? "lessons" : "the map"}</p>

                ) : (
                  <p className="text-xs text-muted-foreground mt-0.5">To track your orders</p>
                )}
              </div>
            </label>
            {createAccount && (
              <div className="relative ml-6">
                <input
                  type={showPassword ? "text" : "password"}
                  value={accountPassword}
                  onChange={(e) => setAccountPassword(e.target.value)}
                  placeholder="Password * (min. 8 characters)"
                  className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                {accountPassword.length > 0 && accountPassword.length < 8 && (
                  <p className="text-xs text-destructive mt-1">Password too short</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Validation errors */}
      {validationErrors.length > 0 && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2.5 space-y-1">
          {validationErrors.map((err, i) => (
            <p key={i} className="text-xs text-destructive flex items-center gap-1.5">
              <span>•</span> {err}
            </p>
          ))}
        </div>
      )}

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={isLoading}
        className="w-full py-3.5 rounded-lg bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
      >
        {isLoading ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Loading...</>
        ) : (
          `Continue • ${fmt(finalTotal)}`
        )}
      </button>

      {bookItems.length > 0 && lessonItems.length > 0 && (
        <p className="text-xs text-muted-foreground text-center">
          ⓘ Books and lessons are processed separately.
        </p>
      )}
    </div>
  );

  // --- EMPTY CART ---
  if (items.length === 0) {
    return createPortal(
      <div className="fixed inset-0 z-[10000] bg-background/95 backdrop-blur-sm flex items-center justify-center overflow-hidden">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors">
          <X className="w-6 h-6" />
        </button>
        <div className="text-center space-y-3">
          <Package className="w-16 h-16 text-muted-foreground/30 mx-auto" />
          <p className="text-muted-foreground">Your cart is empty</p>
          <button onClick={onClose} className="text-sm text-primary underline">Back to shop</button>
        </div>
      </div>,
      document.body
    );
  }

  // --- FULL SCREEN CHECKOUT ---
  return createPortal(
    <div className="fixed inset-0 z-[10000] bg-background overflow-y-auto overflow-x-hidden">
      {/* Close button - desktop only */}
      {!isMobile && (
        <button
          onClick={onClose}
          className="fixed top-4 right-4 z-10 p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
        >
          <X className="w-6 h-6" />
        </button>
      )}

      <div className="max-w-5xl w-full mx-auto px-4 py-8">
        {/* Mobile: always-expanded order summary + close button */}
        {isMobile && (
          <div className="mb-6 border border-border rounded-lg">
            <div className="flex items-center">
              <div className="flex-1 flex items-center justify-between px-4 py-3 text-sm font-medium text-foreground">
                <span>Order summary ({count})</span>
                <span className="font-bold">{fmt(finalTotal)}</span>
              </div>
              <button
                onClick={onClose}
                className="p-2 mr-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-4 pb-4">{orderSummary}</div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Left column */}
          <div className="lg:col-span-3">{leftColumn}</div>

          {/* Right column (desktop only) */}
          {!isMobile && (
            <div className="lg:col-span-2">
              <div className="sticky top-8 border border-border rounded-lg p-5">
                <h2 className="text-base font-semibold text-foreground mb-4">Order summary</h2>
                {orderSummary}
              </div>
            </div>
          )}
        </div>

        <UpsellRecommendations context="cart" className="mt-10" />
      </div>
    </div>,
    document.body
  );
};

export default CartDrawer;
